import { useState, useEffect } from 'react'
import { fetchSchoolBySlug } from './cosmicClient'

/**
 * Custom hook for fetching school data from Cosmic CMS.
 * 
 * Handles loading state, error handling, and automatic refetching when
 * slug or locale changes. Returns the school data along with loading
 * and error states.
 * 
 * @param {string} slug - The school slug to fetch
 * @param {string} locale - The locale language code (default: 'en-US')
 * @returns {Object} Hook state object
 * @returns {Object|null} returns.school - School data object from Cosmic CMS
 * @returns {boolean} returns.loading - True while fetching data
 * @returns {Error|null} returns.error - Error object if fetch failed
 * 
 * @example
 * const { school, loading, error } = useCosmicSchool('harvard-university', 'en-US')
 * 
 * if (loading) return <LoadingSpinner />
 * if (error) return <ErrorMessage />
 * return <SchoolContent data={school} />
 */
export const useCosmicSchool = (slug, locale = 'en-US') => {
  const [school, setSchool] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let isMounted = true

    const fetchSchool = async () => {
      if (!slug) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)
        
        const schoolData = await fetchSchoolBySlug(slug, locale)
        
        if (isMounted) {
          if (!schoolData) {
            setError(new Error(`School not found: ${slug}`))
            setSchool(null)
          } else {
            setSchool(schoolData)
          }
        }
      } catch (err) {
        if (isMounted) {
          console.error('Error fetching school:', err)
          setError(err)
          setSchool(null)
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    fetchSchool()

    return () => {
      isMounted = false
    }
  }, [slug, locale])

  return { school, loading, error }
}

export default useCosmicSchool
